import React, { useEffect } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useGetOnHold } from "modules/invoice/api/src/invoice.api";
import { MdArrowLeft, MdArrowRight } from "react-icons/md";
import { ExternalLinkIcon } from "lucide-react";
import SearchAndFilterBar from "views/admin/default/components/filter/searchAndFilterBar";

type RowObj = {
  _id: string;
  clientId: string;
  clientName: string;
  createdAt: Date;
  dueDate?: Date;
  total: number;
  dueAmount: number;
};

const columnHelper = createColumnHelper<RowObj>();

const formatCurrency = (v: number) =>
  v.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

export const OnHoldInvoiceTable = () => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [searchName, setSearchName] = React.useState("");
  const [filters, setFilters] = React.useState<any>({});
  const {
    data: onHold,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useGetOnHold(searchName, filters);

  const totalPage = onHold?.pages[0]?.count ?? 0;

  useEffect(() => {
    refetch();
  }, [filters, refetch]);

  const columns = [
    columnHelper.accessor("createdAt", {
      id: "Date",
      header: () => <span>Date</span>,
      cell: (info) => (
        <span className="text-gray-600">
          {new Date(info.getValue()).toLocaleDateString("fr-FR")}
        </span>
      ),
    }),
    columnHelper.accessor("clientName", {
      id: "Client",
      header: () => <span>Client</span>,
      cell: (info) => (
        <span className="font-medium text-gray-900">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("total", {
      id: "Montant",
      header: () => <span>Montant</span>,
      cell: (info) => (
        <span className="font-medium text-gray-900">
          {formatCurrency(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("dueAmount", {
      id: "Reste",
      header: () => <span>Reste</span>,
      cell: (info) => {
        const val = info.getValue();
        return (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
              val === 0
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-700"
            }`}
          >
            {formatCurrency(val)}
          </span>
        );
      },
    }),
    columnHelper.accessor("dueDate", {
      id: "Echéance",
      header: () => <span>Echéance</span>,
      cell: (info) => {
        const dueDate = info.getValue();
        if (!dueDate) return <span className="text-gray-400">-</span>;
        const today = new Date();
        const due = new Date(dueDate);
        const diffDays = Math.ceil(
          (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        return (
          <span
            className={`text-sm ${
              diffDays < 0
                ? "font-medium text-red-600"
                : diffDays < 3
                ? "text-amber-600"
                : "text-gray-500"
            }`}
          >
            {diffDays < 0 ? `${Math.abs(diffDays)}j retard` : `${diffDays}j`}
          </span>
        );
      },
    }),
    columnHelper.accessor("_id", {
      id: "Actions",
      header: () => <span></span>,
      cell: (info) => (
        <button
          onClick={() => window.open(`/admin/invoice/${info.getValue()}`, "_blank")}
          className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <ExternalLinkIcon className="h-4 w-4" />
        </button>
      ),
    }),
  ];

  const tableData: RowObj[] = React.useMemo(() => {
    if (!onHold) return [];
    return onHold.pages.flatMap((page) =>
      page.data.map((invoice) => ({
        _id: invoice._id,
        clientId: invoice.clientId,
        clientName: invoice.client?.name ?? "-",
        createdAt: invoice.createdAt,
        dueDate: invoice.dueDate,
        total: invoice.totalTTC,
        dueAmount: invoice.dueAmount ?? invoice.totalHT,
      }))
    );
  }, [onHold]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            En attente
          </h3>
          <p className="text-xs text-gray-400">
            Factures en cours de traitement
          </p>
        </div>
      </div>

      <div className="px-5 pt-3">
        <SearchAndFilterBar
          searchValue={searchName}
          onSearchChange={setSearchName}
          onFilterChange={setFilters}
          type="invoice"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" />
        </div>
      ) : tableData.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-sm text-gray-400">Aucune facture en attente</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className="cursor-pointer px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-50">
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="transition-colors hover:bg-gray-50/50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="whitespace-nowrap px-5 py-3 text-sm">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
            <span className="text-xs text-gray-400">
              Page {currentPage} sur {totalPage}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"
              >
                <MdArrowLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  if (hasNextPage) {
                    fetchNextPage();
                    setCurrentPage(currentPage + 1);
                  }
                }}
                disabled={!hasNextPage}
                className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"
              >
                <MdArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
